import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';

import { Trabajo, Producto, Servicio, PhoneNumber } from '../types';
import { apiCall, API_BASE_URL, formatPrecio } from '../utils';

// ==================== PÁGINA DE TRABAJOS CORREGIDA ====================
export default function TrabajosPage({ navigateToPage }: { navigateToPage?: (page: string) => void }) {
  const [trabajos, setTrabajos] = useState<Trabajo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoria, setSelectedCategoria] = useState<string>('todas');
  const [selectedTrabajo, setSelectedTrabajo] = useState<Trabajo | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageType, setImageType] = useState<'antes' | 'despues'>('despues');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const categorias = [
    { id: 'todas', label: 'Todos', icon: Grid },
    { id: 'cocina', label: 'Cocinas', icon: Home },
    { id: 'baño', label: 'Baños', icon: Bath },
    { id: 'reforma_integral', label: 'Reformas Integrales', icon: Building },
    { id: 'fachada', label: 'Fachadas', icon: Home },
    { id: 'piscina', label: 'Piscinas', icon: Droplets },
    { id: 'aerotermia', label: 'Aerotermia', icon: Thermometer },
    { id: 'impermeabilizacion', label: 'Impermeabilización', icon: CloudRain }
  ];

  useEffect(() => {
    fetchTrabajos();
  }, [selectedCategoria]);

  // En TrabajosPage, después de los otros hooks
    useEffect(() => {
      const handleSelectTrabajo = (event: CustomEvent) => {
        openModal(event.detail);
      };

      window.addEventListener('selectTrabajo', handleSelectTrabajo as EventListener);
      return () => {
        window.removeEventListener('selectTrabajo', handleSelectTrabajo as EventListener);
      };
    }, []);

  // Listener para filtrar por categoría cuando se viene de servicios
    useEffect(() => {
      const handleFilterByCategory = (event: CustomEvent) => {
        setSelectedCategoria(event.detail);
      };

      window.addEventListener('filterTrabajosByCategory', handleFilterByCategory as EventListener);
      return () => {
        window.removeEventListener('filterTrabajosByCategory', handleFilterByCategory as EventListener);
      };
    }, []);

    const fetchTrabajos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE_URL}/trabajos`; 
      if (selectedCategoria !== 'todas') {
        url += `?categoria=${selectedCategoria}`;
      }
      
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setTrabajos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error al cargar trabajos:', error);
      setError('Error al cargar los proyectos');
      setTrabajos([]);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (trabajo: Trabajo) => {
    setSelectedTrabajo(trabajo);
    setCurrentImageIndex(0);
    setImageType('despues');
  };

  const closeModal = () => {
    setSelectedTrabajo(null);
  };

  const nextImage = () => {
    if (!selectedTrabajo) return;
    
    const images = imageType === 'antes' ? selectedTrabajo.imagenes_antes : selectedTrabajo.imagenes_despues;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!selectedTrabajo) return;

    const images = imageType === 'antes' ? selectedTrabajo.imagenes_antes : selectedTrabajo.imagenes_despues;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // ✅ Bloquear el scroll de la página de fondo mientras el modal está abierto.
  // Así el deslizamiento dentro del modal no arrastra la página de detrás (bug móvil).
  useEffect(() => {
    if (selectedTrabajo) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedTrabajo]);

  // ✅ Cerrar el modal con la tecla Escape
  useEffect(() => {
    if (!selectedTrabajo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedTrabajo]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-700 text-white py-32">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1920"
            alt="Proyectos de reformas y construcción"
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-blue-400"></div>
            <span className="text-sm font-semibold text-blue-300 uppercase tracking-widest">Trabajos reales · Antes y después</span>
            <div className="h-[2px] w-10 bg-blue-400"></div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Nuestros Proyectos
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Reformas y obras realizadas en Martos y toda la provincia de Jaén
          </p>
        </div>
      </section>

      {/* Filtros y Controles */}
      <section className="sticky top-20 z-40 bg-white shadow-md py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Categorías */}
            <div className="flex flex-wrap gap-2">
              {categorias.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategoria(cat.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedCategoria === cat.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Vista */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Ver proyectos en cuadrícula"
                aria-pressed={viewMode === 'grid'}
              >
                <Grid className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
                aria-label="Ver proyectos en lista"
                aria-pressed={viewMode === 'list'}
              >
                <List className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contenido Principal */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchTrabajos}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Reintentar
              </button>
            </div>
          )}

          {!loading && !error && trabajos.length === 0 && (
            <div className="max-w-2xl mx-auto py-16 sm:py-20">
              <div className="bg-white border border-gray-200 rounded-2xl px-8 py-12 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-6">
                  <Building className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>

                {selectedCategoria === 'todas' ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Estamos preparando esta sección
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Próximamente publicaremos aquí una selección de nuestros proyectos, con
                      fotografías del antes y el después de cada obra.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Aún no hay proyectos publicados en esta categoría
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Estamos ampliando nuestra galería de proyectos. Puedes consultar el resto
                      de categorías disponibles.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {!loading && !error && trabajos.length > 0 && (
            <div className={viewMode === 'grid' 
              ? "grid md:grid-cols-2 lg:grid-cols-3 gap-8" 
              : "space-y-8"
            }>
              {trabajos.map((trabajo) => (
                <div
                  key={trabajo.id}
                  id={`trabajo-${trabajo.id}`}
                  onClick={() => openModal(trabajo)}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  {/* Imagen */}
                  <div className={`relative overflow-hidden ${
                    viewMode === 'list' ? 'w-1/3' : 'h-64'
                  }`}>
                    <img
                      src={trabajo.imagenes_despues[0] || trabajo.imagenes_antes[0] || '/imagen-no-disponible.svg'}
                      alt={trabajo.titulo}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {trabajo.destacado && (
                      <div className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                        <Star className="h-4 w-4 mr-1" />
                        Destacado
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-medium">
                      {trabajo.categoria.replace('_', ' ')}
                    </div>
                  </div>

                  {/* Contenido */}
                  <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {trabajo.titulo}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {trabajo.descripcion}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {trabajo.ubicacion}
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {trabajo.duracion}
                      </div>
                      {trabajo.presupuesto_aprox && (
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1" />
                          {trabajo.presupuesto_aprox}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center text-blue-600 font-semibold group-hover:text-blue-700">
                      Ver proyecto completo
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal de Proyecto */}
      {selectedTrabajo && (() => {
        const imagenesActuales = imageType === 'antes' ? selectedTrabajo.imagenes_antes : selectedTrabajo.imagenes_despues;
        const tieneAntes = selectedTrabajo.imagenes_antes && selectedTrabajo.imagenes_antes.length > 0;
        const tieneDespues = selectedTrabajo.imagenes_despues && selectedTrabajo.imagenes_despues.length > 0;
        return (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-project-title"
          onClick={closeModal}
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-stretch sm:items-center justify-center z-[70] sm:p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white sm:rounded-2xl w-full sm:max-w-6xl h-full sm:h-auto sm:max-h-[92vh] overflow-y-auto sm:overflow-hidden flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-4 p-5 sm:p-6 border-b border-gray-100 sticky top-0 bg-white z-20 sm:static">
              <div className="min-w-0">
                <span className="inline-block text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
                  {selectedTrabajo.categoria.replace(/_/g, ' ')}
                </span>
                <h2 id="modal-project-title" className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{selectedTrabajo.titulo}</h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                aria-label="Cerrar detalles del proyecto"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Contenido */}
            <div className="grid lg:grid-cols-2 lg:h-[calc(92vh-88px)] lg:overflow-hidden">
              {/* Galería de imágenes */}
              <div className="relative bg-slate-900 flex flex-col">
                {/* Imagen principal */}
                <div className="relative flex-1 flex items-center justify-center p-4 min-h-[280px] sm:min-h-[360px]">
                  <img
                    src={imagenesActuales[currentImageIndex]}
                    alt={`${selectedTrabajo.titulo} - ${imageType}`}
                    className="max-w-full max-h-[45vh] lg:max-h-full object-contain rounded-lg"
                  />

                  {/* Etiqueta antes/después sobre la imagen */}
                  <span className={`absolute top-4 left-4 px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wide text-white shadow-lg ${
                    imageType === 'antes' ? 'bg-slate-600' : 'bg-blue-600'
                  }`}>
                    {imageType === 'antes' ? 'Antes' : 'Después'}
                  </span>

                  {/* Controles de navegación (solo si hay más de una imagen) */}
                  {imagenesActuales.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/15 backdrop-blur text-white p-2.5 rounded-full hover:bg-white/30 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label="Imagen anterior"
                      >
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/15 backdrop-blur text-white p-2.5 rounded-full hover:bg-white/30 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label="Imagen siguiente"
                      >
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </>
                  )}

                  {/* Contador de imágenes */}
                  {imagenesActuales.length > 1 && (
                    <span className="absolute bottom-4 right-4 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {currentImageIndex + 1} / {imagenesActuales.length}
                    </span>
                  )}
                </div>

                {/* Toggle segmentado Antes / Después */}
                {(tieneAntes || tieneDespues) && (
                  <div className="p-4 bg-slate-900 border-t border-white/10">
                    <div className="flex bg-white/10 rounded-lg p-1 max-w-xs mx-auto">
                      <button
                        onClick={() => { setImageType('antes'); setCurrentImageIndex(0); }}
                        disabled={!tieneAntes}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          imageType === 'antes' ? 'bg-white text-slate-900 shadow' : 'text-white hover:bg-white/10'
                        }`}
                      >
                        Antes
                      </button>
                      <button
                        onClick={() => { setImageType('despues'); setCurrentImageIndex(0); }}
                        disabled={!tieneDespues}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          imageType === 'despues' ? 'bg-white text-slate-900 shadow' : 'text-white hover:bg-white/10'
                        }`}
                      >
                        Después
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Información del proyecto */}
              <div className="p-6 sm:p-8 lg:overflow-y-auto">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Descripción del proyecto</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedTrabajo.descripcion}</p>
                  </div>

                  {/* Ficha técnica */}
                  <div className="border-t border-gray-100 pt-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Ficha del proyecto</h3>
                    <dl className="divide-y divide-gray-100">
                      <div className="flex items-center justify-between py-3">
                        <dt className="flex items-center text-gray-500 text-sm">
                          <MapPin className="h-4 w-4 mr-2 text-blue-600" aria-hidden="true" /> Ubicación
                        </dt>
                        <dd className="text-gray-900 font-medium text-sm text-right">{selectedTrabajo.ubicacion}</dd>
                      </div>
                      {selectedTrabajo.duracion && (
                        <div className="flex items-center justify-between py-3">
                          <dt className="flex items-center text-gray-500 text-sm">
                            <Clock className="h-4 w-4 mr-2 text-blue-600" aria-hidden="true" /> Duración
                          </dt>
                          <dd className="text-gray-900 font-medium text-sm text-right">{selectedTrabajo.duracion}</dd>
                        </div>
                      )}
                      {selectedTrabajo.fecha && (
                        <div className="flex items-center justify-between py-3">
                          <dt className="flex items-center text-gray-500 text-sm">
                            <Calendar className="h-4 w-4 mr-2 text-blue-600" aria-hidden="true" /> Fecha
                          </dt>
                          <dd className="text-gray-900 font-medium text-sm text-right">{selectedTrabajo.fecha}</dd>
                        </div>
                      )}
                      {selectedTrabajo.presupuesto_aprox && (
                        <div className="flex items-center justify-between py-3">
                          <dt className="flex items-center text-gray-500 text-sm">
                            <DollarSign className="h-4 w-4 mr-2 text-blue-600" aria-hidden="true" /> Presupuesto aprox.
                          </dt>
                          <dd className="text-gray-900 font-medium text-sm text-right">{selectedTrabajo.presupuesto_aprox}</dd>
                        </div>
                      )}
                    </dl>
                    {selectedTrabajo.presupuesto_aprox && (
                      <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                        * Importe orientativo. El precio final varía según los materiales, el estado previo y las condiciones de cada vivienda.
                      </p>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="bg-blue-50 rounded-xl p-5">
                      <h4 className="font-bold text-gray-900 mb-1">¿Quieres algo parecido?</h4>
                      <p className="text-sm text-gray-600 mb-4">Te preparamos un presupuesto a medida, sin compromiso.</p>
                      <button
                        onClick={() => {
                          closeModal();
                          if (navigateToPage) navigateToPage('contacto');
                        }}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        Solicitar presupuesto
                        <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
